import {
  AttentionBlock,
  QAGroup,
  QAPair,
  ReadingColumn,
  Surface,
  formatCPF,
  titleCase,
} from '@westhetics/ui'
import type { FormEvent, ReactNode, Ref } from 'react'
import type { FormController } from '@/features/clinic-profile/use-form-state'
import { formatPostalCode } from '@/features/clinic-profile/form'
import type { Audience, Patient } from './api'
import { isMinorOn, type PatientFormState } from './form'
import {
  FOREIGN_DOCUMENT_TYPE_LABELS,
  GENDER_LABELS,
  IMAGE_CONSENT_LABELS,
  MARITAL_STATUS_LABELS,
  REFERRAL_CHANNEL_LABELS,
  countryName,
  displayWhatsapp,
  formatDocument,
  isoToBrDate,
  personWords,
} from './labels'
import {
  AddressBlock,
  CivilBlock,
  ConsentBlock,
  ContactBlock,
  GuardianBlock,
  IdentificationBlock,
  OriginBlock,
} from './patient-form-blocks'

/**
 * A aba Dados pessoais da ficha (docs/areas/pacientes.md, "Abas"). Arquétipo: cadastro, em blocos
 * nomeados. A partir de 900px é o formulário, com o Salvar no cabeçalho da ficha; abaixo, o
 * cadastro aparece em leitura e a edição completa continua no computador (RN-PAC-24).
 */

export function PatientDetailsForm({
  formId,
  formRef,
  form,
  audiences,
  isAudienceLocked,
  readOnly,
  sourceClinicName,
  onSubmit,
  notices,
  actions,
  footer,
}: {
  formId: string
  formRef: Ref<HTMLFormElement>
  form: FormController<PatientFormState>
  /** Os públicos da clínica: com mais de um, o campo Público aparece (RN-PAC-36). */
  audiences?: Audience[]
  /** O cadastro já tem documento: o público não muda (RN-PAC-37). */
  isAudienceLocked?: boolean
  /** Cadastro arquivado ou de clínica irmã: os blocos aparecem, sem edição. */
  readOnly: boolean
  /** Cadastro de clínica irmã da rede: o consentimento é o dado a ela (RN-PAC-43). */
  sourceClinicName?: string
  onSubmit: (event: FormEvent) => void
  /** Os erros da ação (salvar, arquivar), acima dos blocos. */
  notices?: ReactNode
  /** Salvar e Cancelar, depois dos blocos (`FormFooter`, T35). */
  actions?: ReactNode
  /** O cartão de arquivar ou restaurar, separado do salvar. */
  footer?: ReactNode
}) {
  return (
    <ReadingColumn wide>
      <form
        id={formId}
        ref={formRef}
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-4"
      >
        {notices}
        <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-4">
          <IdentificationBlock
            form={form}
            readOnly={readOnly}
            audiences={audiences}
            isAudienceLocked={isAudienceLocked}
          />
          <ContactBlock form={form} readOnly={readOnly} />
          <CivilBlock form={form} readOnly={readOnly} />
          <AddressBlock form={form} readOnly={readOnly} />
          <GuardianBlock
            form={form}
            readOnly={readOnly}
            isMinor={isMinorOn(form.values.birthDate)}
          />
          <ConsentBlock form={form} readOnly={readOnly} sourceClinicName={sourceClinicName} />
          <OriginBlock form={form} readOnly={readOnly} />
        </fieldset>
        {actions}
      </form>
      {footer ? <div className="mt-4">{footer}</div> : null}
    </ReadingColumn>
  )
}

const NOT_PROVIDED = 'Não informado'

function addressLines(patient: Patient): string {
  const address = patient.address
  if (!address) return NOT_PROVIDED
  const street = [address.street, address.number].filter(Boolean).join(', ')
  const postalCode =
    address.country === 'BR' ? formatPostalCode(address.postalCode) : address.postalCode
  const place = [address.district, `${address.city}/${address.state}`].filter(Boolean).join(' · ')
  return [
    address.complement ? `${street} · ${address.complement}` : street,
    place,
    address.country === 'BR'
      ? `CEP ${postalCode}`
      : `${postalCode} · ${countryName(address.country)}`,
  ].join(' · ')
}

function documentAnswer(patient: Patient): { question: string; answer: string } {
  if (patient.docNumber) {
    return {
      question: patient.docType === 'cnpj' ? 'CNPJ' : 'CPF',
      answer: formatDocument(patient.docNumber, patient.docType),
    }
  }
  if (patient.foreignDocument) {
    const { type, number, country } = patient.foreignDocument
    return {
      question: FOREIGN_DOCUMENT_TYPE_LABELS[type],
      answer: `${number} · ${countryName(country)}`,
    }
  }
  return { question: personWords(patient.audience).document, answer: NOT_PROVIDED }
}

/** O cadastro em leitura, para o celular: o que a clínica tem, sem formulário apertado. */
export function PatientDetailsRead({
  patient,
  notices,
  footer,
}: {
  patient: Patient
  notices?: ReactNode
  footer?: ReactNode
}) {
  const idDocument = documentAnswer(patient)
  const birth = patient.birthDate
    ? `${isoToBrDate(patient.birthDate)}${patient.age !== null ? ` · ${patient.age === 1 ? '1 ano' : `${patient.age} anos`}` : ''}`
    : NOT_PROVIDED
  return (
    <ReadingColumn wide className="flex flex-col gap-4">
      {notices}
      {patient.status === 'active' && !patient.isReadOnly ? (
        <AttentionBlock variant="lilac" title="A edição do cadastro continua no computador">
          Aqui você confere os dados. Para completar ou corrigir, abra esta ficha num computador: o
          medidor acima diz o que falta.
        </AttentionBlock>
      ) : null}
      <Surface>
        <QAGroup title="Identificação" className="border-t-0 pt-0">
          <QAPair question="Nome completo" answer={titleCase(patient.name)} />
          <QAPair question="Data de nascimento" answer={birth} />
          <QAPair question={idDocument.question} answer={idDocument.answer} />
          <QAPair question="RG" answer={patient.rg ?? NOT_PROVIDED} />
        </QAGroup>
        <QAGroup title="Contato" className="mt-4">
          <QAPair question="WhatsApp" answer={displayWhatsapp(patient.whatsapp) || NOT_PROVIDED} />
          <QAPair question="E-mail" answer={patient.email ?? NOT_PROVIDED} />
          {patient.instagram ? <QAPair question="Instagram" answer={patient.instagram} /> : null}
        </QAGroup>
        <QAGroup title="Estado civil e profissão" className="mt-4">
          <QAPair
            question="Estado civil"
            answer={
              patient.maritalStatus ? MARITAL_STATUS_LABELS[patient.maritalStatus] : NOT_PROVIDED
            }
          />
          <QAPair question="Profissão" answer={patient.occupation ?? NOT_PROVIDED} />
          {patient.gender !== 'not_provided' ? (
            <QAPair question="Gênero" answer={GENDER_LABELS[patient.gender]} />
          ) : null}
        </QAGroup>
        <QAGroup title="Endereço" className="mt-4">
          <QAPair question="Endereço" answer={addressLines(patient)} />
        </QAGroup>
        {patient.guardian ? (
          <QAGroup title="Responsável" className="mt-4">
            <QAPair question="Nome do responsável" answer={titleCase(patient.guardian.name)} />
            <QAPair question="CPF do responsável" answer={formatCPF(patient.guardian.docNumber)} />
            <QAPair
              question="WhatsApp do responsável"
              answer={displayWhatsapp(patient.guardian.whatsapp)}
            />
          </QAGroup>
        ) : null}
        <QAGroup title="Consentimentos" className="mt-4">
          <QAPair
            question="Uso de imagem"
            answer={
              patient.sourceClinic
                ? `${IMAGE_CONSENT_LABELS[patient.imageConsent]} · vale só para ${patient.sourceClinic.name}`
                : IMAGE_CONSENT_LABELS[patient.imageConsent]
            }
          />
        </QAGroup>
        {patient.referralSource || patient.notes ? (
          <QAGroup title="Origem e observações" className="mt-4">
            {patient.referralSource ? (
              <QAPair
                question="Como chegou à clínica"
                answer={[
                  REFERRAL_CHANNEL_LABELS[patient.referralSource.channel],
                  patient.referralSource.detail,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            ) : null}
            {patient.notes ? (
              // Texto corrido não vai em Lexend: as observações saem como parágrafo.
              <div className="py-3">
                <div className="eyebrow">Observações internas</div>
                <p className="font-body text-body text-ink mt-2 font-light whitespace-pre-line">
                  {patient.notes}
                </p>
              </div>
            ) : null}
          </QAGroup>
        ) : null}
      </Surface>
      {footer}
    </ReadingColumn>
  )
}
